import { useState, useCallback, useRef, type FormEvent, type KeyboardEvent } from 'react';
import { Button, Card } from '../ui';

export interface VerificationScreenProps {
  /** The email address the verification was sent to */
  email?: string;
  /** Called when user submits verification code */
  onVerify?: (code: string) => Promise<void>;
  /** Called when user requests resend of verification email */
  onResend?: () => Promise<void>;
  /** Called when user navigates back to login */
  onNavigateLogin?: () => void;
}

const CODE_LENGTH = 6;

/**
 * Email verification screen with code input and resend option.
 * User enters the 6-digit verification code sent to their email.
 *
 * @see Requirements 7.3, 7.8
 */
export function VerificationScreen({
  email,
  onVerify,
  onResend,
  onNavigateLogin,
}: VerificationScreenProps) {
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fullCode = code.join('');
  const isCodeComplete = fullCode.length === CODE_LENGTH;

  const handleInputChange = useCallback(
    (index: number, value: string) => {
      // Only accept digits
      const digit = value.replace(/\D/g, '').slice(-1);

      setCode((prev) => {
        const next = [...prev];
        next[index] = digit;
        return next;
      });

      // Auto-advance to next input
      if (digit && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    []
  );

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [code]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
      if (pasted.length > 0) {
        const newCode = Array(CODE_LENGTH).fill('');
        for (let i = 0; i < pasted.length; i++) {
          newCode[i] = pasted[i];
        }
        setCode(newCode);
        // Focus the next empty input or the last one
        const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
        inputRefs.current[focusIndex]?.focus();
      }
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!isCodeComplete) {
        setError('Please enter the full verification code');
        return;
      }

      setIsLoading(true);
      try {
        await onVerify?.(fullCode);
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Verification failed. Please check your code and try again.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [fullCode, isCodeComplete, onVerify]
  );

  const handleResend = useCallback(async () => {
    setError(null);
    setResendSuccess(false);

    try {
      await onResend?.();
      setResendSuccess(true);

      // Start 60-second cooldown
      setResendCooldown(60);
      cooldownRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch {
      setError('Failed to resend verification email. Please try again.');
    }
  }, [onResend]);

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card>
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center">
              <h1 className="text-heading-1 text-text-heading font-bold">Verify Email</h1>
              <p className="mt-2 text-sm text-text-secondary">
                {email ? (
                  <>
                    We sent a verification code to{' '}
                    <span className="font-medium text-text-primary">{email}</span>
                  </>
                ) : (
                  'Enter the verification code sent to your email'
                )}
              </p>
            </div>

            {/* Status messages */}
            <div aria-live="polite" aria-atomic="true">
              {error && (
                <div
                  className="rounded-sm bg-primary-light/30 border border-primary-dark/20 px-4 py-3 text-sm text-primary-dark"
                  role="alert"
                >
                  {error}
                </div>
              )}
              {resendSuccess && !error && (
                <div
                  className="rounded-sm bg-secondary-light/30 border border-secondary/30 px-4 py-3 text-sm text-text-primary"
                  role="status"
                >
                  Verification email resent successfully.
                </div>
              )}
            </div>

            {/* Code input */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <fieldset>
                <legend className="sr-only">Verification code</legend>
                <div className="flex justify-center gap-2" role="group" aria-label="Verification code input">
                  {Array.from({ length: CODE_LENGTH }).map((_, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]"
                      maxLength={1}
                      value={code[index]}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      disabled={isLoading}
                      className="w-12 h-14 text-center text-lg font-semibold rounded-sm border border-border bg-bg-card text-text-primary transition-all duration-fast focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-secondary disabled:opacity-50"
                      aria-label={`Digit ${index + 1}`}
                    />
                  ))}
                </div>
              </fieldset>

              <Button
                type="submit"
                className="w-full"
                disabled={!isCodeComplete || isLoading}
              >
                {isLoading ? 'Verifying...' : 'Verify Email'}
              </Button>
            </form>

            {/* Resend option */}
            <div className="text-center">
              <p className="text-sm text-text-secondary">
                Didn&apos;t receive the code?{' '}
                {resendCooldown > 0 ? (
                  <span className="text-text-secondary">
                    Resend in {resendCooldown}s
                  </span>
                ) : (
                  <button
                    type="button"
                    className="text-primary-dark font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded-sm"
                    onClick={handleResend}
                    disabled={isLoading}
                  >
                    Resend code
                  </button>
                )}
              </p>
            </div>

            {/* Back to login */}
            <p className="text-center text-sm text-text-secondary">
              <button
                type="button"
                className="text-primary-dark font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded-sm"
                onClick={onNavigateLogin}
              >
                Back to sign in
              </button>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
